import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { Spinner, Empty } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import ReglamentoVisor, { fechaLarga, fechaHora, imprimirPdf } from "@/components/ReglamentoVisor";
import { BookOpen, CheckCircle2, Download, Eye, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function Constancia({ data, onConfirmar, busy }) {
  const c = data.confirmacion;
  if (c)
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="rit-confirmado">
        <div className="flex items-start gap-3 text-sm text-emerald-900">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div><p className="font-semibold">Recepción confirmada · versión {c.version}</p><p className="text-emerald-800/80">{fechaHora(c.fecha)} · Constancia <span className="font-mono" data-testid="rit-codigo-constancia">{c.codigo}</span></p></div>
        </div>
        <button onClick={() => downloadFile(`/reglamento/constancia/${c.id}/pdf`, `constancia_reglamento_${c.codigo}.pdf`)} data-testid="rit-descargar-constancia-btn"
          className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"><Download className="h-4 w-4" /> Descargar constancia</button>
      </div>
    );
  return (
    <div className="rounded-xl border border-vino-200 bg-vino-50/60 p-4 sm:p-5" data-testid="rit-declaracion">
      <p className="text-sm font-medium leading-relaxed text-slate-800">“{data.declaracion}”</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button onClick={onConfirmar} disabled={busy} data-testid="rit-confirmar-btn"
          className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-vino-800 disabled:opacity-60">
          {busy ? <Spinner className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />} Confirmar recepción
        </button>
        <p className="text-xs text-slate-500">Es una constancia de acceso y recepción, no una renuncia a derechos. Puedes consultar el reglamento aunque no confirmes.</p>
      </div>
    </div>
  );
}

export default function PortalReglamento() {
  const { user } = useAuth();
  const esEmpleado = user?.role === "empleado";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bajando, setBajando] = useState(false);
  const [verPdf, setVerPdf] = useState(false);
  const load = () => api.get("/reglamento/vigente").then((r) => setData(r.data)).catch(() => setData({ version: null }));
  useEffect(() => { load(); }, []);

  const confirmar = async () => {
    setBusy(true);
    try {
      await api.post("/reglamento/confirmar", { version_id: data.version.id });
      toast.success("Recepción confirmada. Tu constancia quedó registrada.");
      window.dispatchEvent(new Event("rit-confirmado"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); load(); }
    finally { setBusy(false); }
  };
  const descargar = async () => {
    setBajando(true);
    try { await downloadFile("/reglamento/vigente/pdf", `reglamento_interno_trabajo_v${data.version.version}.pdf`); }
    catch { toast.error("No se pudo descargar el PDF. Intenta nuevamente."); }
    finally { setBajando(false); }
  };

  if (!data) return <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  const v = data.version;
  return (
    <div className="space-y-5" data-testid="portal-reglamento">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Reglamento Interno de Trabajo</h1>
        <p className="mt-1 text-sm text-slate-500">HUMAN RESOURCES MANAGMENT SA · {esEmpleado ? "disponible para tu consulta permanente durante toda tu vinculación." : "versión vigente, disponible para consulta y descarga."}</p>
      </div>
      {!v ? <Empty icon={BookOpen} title="Reglamento en preparación" subtitle="HUMAN publicará aquí el Reglamento Interno de Trabajo." /> : (
        <>
          <p className="text-xs text-slate-500" data-testid="rit-meta">Versión <strong className="text-slate-700">{v.version}</strong> · Publicado el {fechaLarga(v.fecha_publicacion)} · Vigente desde el {fechaLarga(v.fecha_vigencia)}</p>
          {esEmpleado && <Constancia data={data} onConfirmar={confirmar} busy={busy} />}
          {v.formato === "html" ? <ReglamentoVisor html={data.html} membrete={data.membrete} onDescargar={descargar} descargando={bajando} onImprimir={() => imprimirPdf("/reglamento/vigente/pdf").catch(() => toast.error("No se pudo preparar la impresión"))} /> : (
            <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-4">
              <button onClick={() => setVerPdf(true)} data-testid="rit-ver-pdf-btn" className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-2 text-sm font-semibold text-white hover:bg-vino-800"><Eye className="h-4 w-4" /> Ver reglamento</button>
              <button onClick={descargar} disabled={bajando} data-testid="rit-descargar-pdf-btn" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Descargar PDF</button>
            </div>
          )}
          {verPdf && <DocViewer url="/reglamento/vigente/pdf" titulo="Reglamento Interno de Trabajo" subtitulo={`Versión ${v.version}`} nombreArchivo={`reglamento_interno_trabajo_v${v.version}.pdf`} onClose={() => setVerPdf(false)} />}
        </>
      )}
    </div>
  );
}
